// Split from theme.js: Google Maps components (lines 4145-4318)
class GMap extends HTMLElement {
  constructor() {
    super()

    if (!this.hasAttribute('data-api-key') || !this.hasAttribute('data-map-address')) {
      return
    }

    Motion.inView(this, this.prepMapApi.bind(this), { margin: '600px 0px 600px 0px' })

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {
      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError)
      }
    }

    window.mapError = (error, element) => {
      const container = element ? element.closest('.shopify-section') : document
      container.querySelectorAll('.gmap--error').forEach((error) => {
        error.remove()
      })

      container.querySelectorAll('g-map').forEach((map) => {
        const message = document.createElement('div')
        message.classList.add('rte', 'alert', 'alert--error', 'gmap--error')
        message.innerHTML = error
        map.closest('.with-map').prepend(message)
      })
    }

    window.gmNoop = () => {}
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest('.banner__map')
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove('opacity-0')
          }
        }, 1e3)
      })
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      document.body.appendChild(script)
      script.onload = resolve
      script.onerror = reject
      script.async = true
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.getAttribute('data-api-key')}&callback=gmNoop`
    })
  }

  initMap() {
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ address: this.getAttribute('data-map-address') }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        // Show errors only to merchant in the editor.
        if (Shopify.designMode) {
          let errorMessage

          switch (status) {
            case 'ZERO_RESULTS':
              errorMessage = theme.strings.addressNoResults
              break
            case 'OVER_QUERY_LIMIT':
              errorMessage = theme.strings.addressQueryLimit
              break
            case 'REQUEST_DENIED':
              errorMessage = theme.strings.authError
              break
            default:
              errorMessage = theme.strings.addressError
              break
          }
          window.mapError(errorMessage, this)
        }
      } else {
        const mapOptions = {
          zoom: parseInt(this.getAttribute('data-zoom')),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        }

        const map = new google.maps.Map(this, mapOptions),
          center = map.getCenter()

        map.setCenter(center)

        const icon = {
          path: 'M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z',
          fillColor: this.getAttribute('data-marker-color'),
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.7
        }

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        })

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML))

        map.mapTypes.set('styled_map', styledMapType)
        map.setMapTypeId('styled_map')
      }
    })
  }
}
customElements.define('g-map', GMap)

class GMapLocations extends HTMLUListElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex
    this.buttons.forEach((button, index) => button.addEventListener('click', () => (this.selectedIndex = index)))

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => (this.selectedIndex = this.buttons.indexOf(event.target)))
    }
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString())
  }

  get buttons() {
    return (this._buttons =
      this._buttons || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children))
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('active', index === parseInt(newValue))
      button.querySelector('[data-map-button]')?.classList.toggle('button--secondary', index !== parseInt(newValue))
    })

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      if (this.controlledElement) {
        const button = this.buttons[parseInt(newValue)]
        this.controlledElement.setAttribute('data-map-address', button.getAttribute('data-map-address'))
        this.controlledElement.initMap()
      }
    }
  }
}
customElements.define('g-map-locations', GMapLocations, { extends: 'ul' })
